import { Box } from '@mantine/core';
import { forwardRef, useState, useRef, useCallback } from 'react';
import { useReducedMotion } from '../hooks';

export interface TiltOnHoverProps {
  children: React.ReactNode;
  maxTilt?: number;
  perspective?: number;
  scale?: number;
  duration?: number;
  reset?: boolean;
}

const TiltOnHover = forwardRef<HTMLDivElement, TiltOnHoverProps>(
  (
    {
      children,
      maxTilt = 15,
      perspective = 1000,
      scale = 1.02,
      duration = 200,
      reset = true,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const containerRef = useRef<HTMLDivElement | null>(null) as React.MutableRefObject<HTMLDivElement | null>;
    const [transform, setTransform] = useState('');

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (reducedMotion || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const mouseX = e.clientX - centerX;
        const mouseY = e.clientY - centerY;

        const rotateX = (mouseY / (rect.height / 2)) * -maxTilt;
        const rotateY = (mouseX / (rect.width / 2)) * maxTilt;

        setTransform(
          `perspective(${perspective}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scale})`
        );
      },
      [maxTilt, perspective, scale, reducedMotion]
    );

    const handleMouseLeave = useCallback(() => {
      if (reset) {
        setTransform('');
      }
    }, [reset]);

    return (
      <Box
        ref={(node) => {
          containerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: transform || 'perspective(1000px) rotateX(0) rotateY(0) scale(1)',
          transition: reducedMotion
            ? 'none'
            : transform
              ? 'none'
              : `transform ${duration}ms ease-out`,
          transformStyle: 'preserve-3d',
        }}
      >
        {children}
      </Box>
    );
  }
);

TiltOnHover.displayName = 'TiltOnHover';

export default TiltOnHover;
